import type { AggregatedData, DataLoaderResult } from '@/types'

// Load full aggregated.json which contains all tribe/break combinations
const AGGREGATED_URL = '/data/aggregated.json'

// Cache-busting: append build timestamp to bypass browser/CDN caches
const cacheBuster = `?v=${__BUILD_TIME__}`

let cachedData: AggregatedData | null = null
let cachedTribeData: Record<string, Record<string, { responses: Array<{ label: string; percentage: number }>; sampleSize: number }>> = {}

/**
 * Load aggregated survey data.
 * Loads full aggregated.json which contains all tribe and break combinations.
 */
export async function loadAggregatedData(): Promise<DataLoaderResult> {
  const startTime = performance.now()

  if (cachedData) {
    return {
      data: cachedData,
      loadTime: performance.now() - startTime,
    }
  }

  try {
    const response = await fetch(AGGREGATED_URL + cacheBuster, {
      cache: 'no-store',  // Force bypass browser cache
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to load data: ${response.status} ${response.statusText}`)
    }

    const data: AggregatedData = await response.json()

    if (!data.meta || !data.questions || !data.tribes) {
      throw new Error('Invalid data format: missing required fields')
    }

    cachedData = data

    console.log(`[DataLoader] Loaded full data in ${(performance.now() - startTime).toFixed(0)}ms`)

    return {
      data,
      loadTime: performance.now() - startTime,
    }
  } catch (error) {
    throw new Error(
      `Failed to load dashboard data: ${error instanceof Error ? error.message : 'Unknown error'}`
    )
  }
}

/**
 * Load tribe-specific data on demand.
 * Called when user selects a specific tribe filter.
 */
export async function loadTribeData(tribeId: string): Promise<Record<string, { responses: Array<{ label: string; percentage: number }>; sampleSize: number }>> {
  if (tribeId === 'total') {
    // Total data already loaded in initial load
    return {}
  }

  if (cachedTribeData[tribeId]) {
    return cachedTribeData[tribeId]
  }

  try {
    const response = await fetch(`/data/tribes/${tribeId}.json${cacheBuster}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      }
    })
    if (response.ok) {
      const data = await response.json()
      cachedTribeData[tribeId] = data
      console.log(`[DataLoader] Loaded tribe data: ${tribeId}`)
      return data
    }
  } catch (error) {
    console.warn(`[DataLoader] Failed to load tribe ${tribeId}:`, error)
  }

  return {}
}

/**
 * Get question data for a specific filter combination.
 * Merges cached tribe data if available.
 */
export function getQuestionData(
  questionId: string,
  tribeId: string = 'total',
  breakId: string = 'total',
  breakValue?: string
): { responses: Array<{ label: string; percentage: number }>; sampleSize: number } | null {
  if (!cachedData) return null

  const question = cachedData.questions[questionId]
  if (!question) return null

  // Build filter key
  let filterKey = `tribe:${tribeId}|break:${breakId}`
  if (breakValue && breakId !== 'total') {
    filterKey = `tribe:${tribeId}|break:${breakId}:${breakValue}`
  }

  // Check question data first
  if (question.data[filterKey]) {
    return question.data[filterKey]
  }

  // Check cached tribe data
  if (tribeId !== 'total' && cachedTribeData[tribeId]?.[questionId]) {
    return cachedTribeData[tribeId][questionId]
  }

  return null
}

/**
 * Clear cached data (useful for testing or forced refresh)
 */
export function clearDataCache(): void {
  cachedData = null
  cachedTribeData = {}
}

/**
 * Check if data is already cached
 */
export function isDataCached(): boolean {
  return cachedData !== null
}
